/**
 * 岐阜県の背景ツアー「岐阜めぐり」コンテンツ。
 *
 * 軸は一日の時間。朝の天空の茶畑から、夏の夜の長良川鵜飼へ。
 * 飛騨に4か所、美濃・東濃・西濃に6か所を配している。
 *
 * 白川郷について。
 *  世界遺産の登録名称は「白川郷・五箇山の合掌造り集落」で、白川郷だけではない。
 *  白川村側の対象は荻町。豆知識では富山県の五箇山とあわせて登録されたと書く。
 *  集落の中には白川八幡神社と明善寺（墓地あり）があるので、
 *  絵は展望台からの俯瞰にして、鳥居も寺も墓も入れない。
 *  現存する合掌造りの戸数は村公式で確認できなかった。書かない。
 *
 * 岐阜城について。
 *  天守閣は令和8年5月19日から令和9年10月下旬まで休館している。
 *  「登れる」「天守から見える」と書けないので、今回は景色から外した。
 *
 * 限定語を落とさない。
 *  - 乗鞍は「バスで走れる標高としては日本一」（乗鞍スカイライン公式）。
 *    「日本一高い道路」と書かない。
 *  - 養老の滝は「日本の三名瀑の一つに数えられています」（養老町公式）。
 *    「日本三名瀑」と断定しない。
 *  - 宗祇水は「第一回名水百選に選定された」。「名水百選の第1号」と書かない。
 *
 * そのほか調査で出てきた「書いてはいけないこと」。
 *  - 淡墨ザクラの樹高は本巣市（17.3メートル・2019年測定）と県（28メートル）で食い違う。
 *    新しい実測値である本巣市の数値だけを使い、両方を混ぜない。
 *  - 新穂高ロープウェイの「日本唯一の2階建てゴンドラ」は公式で確認できなかった。
 *  - 恵那峡の奇岩の名前と名勝指定は公式で確認できなかった。
 *  - 関ケ原は東首塚・西首塚という供養の場を含むので、今回は扱わない。
 *  - 養老公園の養老天命反転地は現代美術作品。絵に入れない。
 *  - 拝観料・開館時間・祭りの日程など、時間で変わるものは書かない。
 */

import type { StageTour } from '../types';

export const gifuSceneryTour: StageTour = {
  scenes: [
    {
      id: 'tenku-no-chabatake',
      name: '天空の茶畑',
      area: '揖斐川町・朝',
      image: '/assets/scenery/gifu/scenes/01_tenku_no_chabatake.webp',
      hotspots: [
        {
          id: 'chabatake-yabukita',
          x: 0.5,
          y: 0.52,
          title: '昭和55年からのやぶきた',
          body: '昭和35年までは養蚕が盛んで桑の畑でしたが、のちに茶畑に転換されました。昭和55年から「やぶきた」品種の栽培が始まり、現在の生産の六割を占めています。',
          source: 'https://www.town.ibigawa.lg.jp/site/kankou/2420.html',
          mapQuery: '天空の茶畑 揖斐川町',
        },
        {
          id: 'chabatake-machupicchu',
          x: 0.24,
          y: 0.66,
          title: '標高約300メートル',
          body: '南米マチュピチュのような高地という点から「天空の茶畑」「岐阜のマチュピチュ」と呼ばれます。標高は約300メートルで、駐車場から絶景ポイントまでは徒歩で約20分かかります。',
          source: 'https://www.town.ibigawa.lg.jp/site/kankou/2420.html',
          mapQuery: '上ヶ流 茶畑',
        },
        {
          id: 'chabatake-minoibi',
          x: 0.76,
          y: 0.34,
          title: '美濃いび茶',
          body: '美濃茶の二大銘柄は美濃いび茶と美濃白川茶で、揖斐地域は揖斐川町と池田町。旧春日村では特別栽培茶の生産が行われています。',
          source: 'https://www.pref.gifu.lg.jp/page/3137.html',
          mapQuery: '美濃いび茶',
        },
      ],
    },
    {
      id: 'shirakawago',
      name: '白川郷 合掌造り集落',
      area: '白川村・冬の朝',
      image: '/assets/scenery/gifu/scenes/02_shirakawago.webp',
      hotspots: [
        {
          id: 'shirakawago-isan',
          x: 0.5,
          y: 0.5,
          title: '白川郷・五箇山の合掌造り集落',
          body: '1995年12月に「白川郷・五箇山の合掌造り集落」としてユネスコの世界遺産へ登録されることが決まりました。対象は岐阜県白川村荻町、富山県平村相倉、同県上平村菅沼の三つの集落です。',
          source: 'https://www.vill.shirakawa.lg.jp/1230.htm',
          mapQuery: '白川郷 荻町',
        },
        {
          id: 'shirakawago-gassho',
          x: 0.24,
          y: 0.36,
          title: '南北に向けて建てる',
          body: '合掌造りは木材を梁の上で手の平を合わせたように山形に組み、勾配の急な茅葺きの屋根をもつ住居です。建物は南北に面して建てられており、風の抵抗を最小限にしつつ、屋根に当たる日照量を調節しています。小屋内は2〜4層に分けて蚕の飼育場に使われていました。',
          source: 'https://www.vill.shirakawa.lg.jp/1231.htm',
          mapQuery: '合掌造り',
        },
        {
          id: 'shirakawago-3gensoku',
          x: 0.76,
          y: 0.64,
          title: '売らない・貸さない・壊さない',
          body: '大正13年に約300棟あった合掌建物は昭和36年に190棟へ激減しました。昭和46年には地域内の資源を「売らない」「貸さない」「壊さない」の3原則を掲げ、昭和51年に国の重要伝統的建造物保存地区に選定されました。',
          source: 'https://www.vill.shirakawa.lg.jp/1411.htm',
          mapQuery: '白川村',
        },
      ],
    },
    {
      id: 'takayama-sanmachi',
      name: '高山市三町の町並み',
      area: '高山市・午前',
      image: '/assets/scenery/gifu/scenes/03_takayama_sanmachi.webp',
      hotspots: [
        {
          id: 'sanmachi-sentei',
          x: 0.5,
          y: 0.5,
          title: '昭和54年に国の選定',
          body: '高山市三町伝統的建造物群保存地区は、市指定が昭和52年3月30日、国指定が昭和54年2月3日、追加選定が平成9年5月29日です。',
          source:
            'https://www.city.takayama.lg.jp/kurashi/1000021/1000119/1000847/1001005/1001006.html',
          mapQuery: '高山市三町',
        },
        {
          id: 'sanmachi-menseki',
          x: 0.24,
          y: 0.36,
          title: '約4.4ヘクタール',
          body: '規模は約4.4ヘクタール、南北約420メートル、東西約150メートル。伝統的建造物は172棟、その他の工作物が2棟あります。',
          source:
            'https://www.city.takayama.lg.jp/kurashi/1000021/1000119/1000847/1001005/1001006.html',
          mapQuery: '高山 古い町並み',
        },
        {
          id: 'sanmachi-mise',
          x: 0.76,
          y: 0.64,
          title: '屋根高4.5メートル',
          body: '道路に面した部屋を「ミセ」と呼びます。屋根高は4.5メートルと低く、建物の間口は3間から4間と狭く、奥に深い構造です。',
          source:
            'https://www.city.takayama.lg.jp/kurashi/1000021/1000119/1000847/1001005/1001006.html',
          mapQuery: '高山 三町 町家',
        },
      ],
    },
    {
      id: 'usuzumi-zakura',
      name: '根尾谷淡墨ザクラ',
      area: '本巣市・4月中旬',
      image: '/assets/scenery/gifu/scenes/04_usuzumi_zakura.webp',
      hotspots: [
        {
          id: 'usuzumi-3iro',
          x: 0.5,
          y: 0.44,
          title: '三度色が変わる',
          body: '桜の全種300余種のなかでも名花中の上位にあるといわれる品種です。蕾のときは薄いピンク、満開に至っては白色、散りぎわには特異の淡い墨色を帯びてきます。',
          source: 'https://www.city.motosu.lg.jp/0000000302.html',
          mapQuery: '淡墨桜',
        },
        {
          id: 'usuzumi-jumoku',
          x: 0.24,
          y: 0.6,
          title: '樹齢1,500余年',
          body: '品種は彼岸桜。樹齢1,500余年、樹高17.3メートル（2019年1月測定）、幹囲は目通り9.4メートル、枝張りは東西22.4メートル・南北24.2メートルです。',
          source: 'https://www.city.motosu.lg.jp/0000000302.html',
          mapQuery: '根尾谷 淡墨桜',
        },
        {
          id: 'usuzumi-shitei',
          x: 0.76,
          y: 0.3,
          title: '大正11年に天然記念物',
          body: '大正11年10月12日に国の天然記念物に指定されました。第26代継体天皇が都へ上る際にこの地を忘れないため植えられたと伝わります。',
          source: 'https://www.pref.gifu.lg.jp/page/367133.html',
          mapQuery: '淡墨公園',
        },
      ],
    },
    {
      id: 'norikura-tatamidaira',
      name: '乗鞍岳 畳平',
      area: '高山市・正午',
      image: '/assets/scenery/gifu/scenes/05_norikura_tatamidaira.webp',
      hotspots: [
        {
          id: 'norikura-kengamine',
          x: 0.5,
          y: 0.36,
          title: '剣ヶ峰3026メートル',
          body: '乗鞍岳の最高峰は剣ヶ峰で標高3026メートル。乗鞍岳は23の峰と7つの湖、8つの平原から成ります。',
          source: 'https://norikuradake.jp/about/',
          mapQuery: '乗鞍岳 剣ヶ峰',
        },
        {
          id: 'norikura-2702',
          x: 0.24,
          y: 0.56,
          title: 'バスで走れる標高としては日本一',
          body: '畳平バスターミナルは標高2702メートルで、バスで走れる標高としては日本一です。マイカー規制のためシャトルバスだけが入れます。',
          source: 'https://norikuradake.jp/about/',
          mapQuery: '乗鞍 畳平',
        },
        {
          id: 'norikura-kaika',
          x: 0.76,
          y: 0.62,
          title: '7月中旬から8月下旬',
          body: '乗鞍スカイラインの開通期間は5月15日から10月31日で、開通日から6月中旬頃までは雪の壁が見られます。高山植物の見頃は7月中旬から8月下旬で、ハクサンイチゲの群生などが広がります。',
          source: 'https://norikuradake.jp/about/',
          mapQuery: '乗鞍スカイライン',
        },
      ],
    },
    {
      id: 'yoro-no-taki',
      name: '養老の滝',
      area: '養老町・午後',
      image: '/assets/scenery/gifu/scenes/06_yoro_no_taki.webp',
      hotspots: [
        {
          id: 'yoro-sunpo',
          x: 0.5,
          y: 0.44,
          title: '高さ約30メートル',
          body: '高さ約30メートル、巾約4メートルの滝で、標高は約280メートルのところにあります。',
          source: 'https://www.town.yoro.gifu.jp/tourism/sightseeing/1002041.html',
          mapQuery: '養老の滝',
        },
        {
          id: 'yoro-hyakusen',
          x: 0.24,
          y: 0.6,
          title: '日本の滝百選',
          body: '日本の滝百選にも選定され、日本の三名瀑の一つに数えられています。',
          source: 'https://www.town.yoro.gifu.jp/tourism/sightseeing/1002041.html',
          mapQuery: '養老公園',
        },
        {
          id: 'yoro-meisui',
          x: 0.76,
          y: 0.3,
          title: '年号になった湧き水',
          body: '元正天皇がこの地を訪れ、湧水を「老を養う若返りの水」と称して年号を養老と改められたと伝わります。貧しいきこりが岩から山吹色の水が湧き出るのを見つけ、それが酒の味がしたという伝説も残ります。養老公園は明治13年に開園しました。',
          source: 'https://www.pref.gifu.lg.jp/page/3157.html',
          mapQuery: '養老の滝 菊水泉',
        },
      ],
    },
    {
      id: 'naegijo',
      name: '苗木城跡',
      area: '中津川市・午後',
      image: '/assets/scenery/gifu/scenes/07_naegijo.webp',
      hotspots: [
        {
          id: 'naegi-shiseki',
          x: 0.5,
          y: 0.46,
          title: '国の史跡',
          body: '苗木城跡は昭和56年4月22日に国の史跡に指定されました。所在地は中津川市苗木字櫓下です。',
          source: 'https://www.pref.gifu.lg.jp/page/366187.html',
          mapQuery: '苗木城跡',
        },
        {
          id: 'naegi-takamori',
          x: 0.24,
          y: 0.62,
          title: '高森山の頂',
          body: '木曽川右岸にひときわ高くそびえる高森山（標高432メートル）の頂部にあり、三方は山に囲まれて絶壁をなしています。本丸は約330平方メートル、内郭部分は約15,000平方メートル。本丸の天守は約7メートル四方で三層からなったと伝わります。',
          source: 'https://www.pref.gifu.lg.jp/page/366187.html',
          mapQuery: '苗木城 天守跡',
        },
        {
          id: 'naegi-toyama',
          x: 0.76,
          y: 0.3,
          title: '遠山氏の270年',
          body: '大永年中（1521から1528）に遠山一雲入道昌利が福岡村植苗木の城を高森に移築しました。天正11年（1583）に落城しましたが、慶長5年（1600）に旧領が復活し、以後明治2年（1869）まで12代270年にわたり遠山氏が支配しました。',
          source: 'https://www.pref.gifu.lg.jp/page/366187.html',
          mapQuery: '苗木城',
        },
      ],
    },
    {
      id: 'setogawa-shirakabe',
      name: '瀬戸川と白壁土蔵街',
      area: '飛騨市・夕方前',
      image: '/assets/scenery/gifu/scenes/08_setogawa_shirakabe.webp',
      hotspots: [
        {
          id: 'setogawa-koi',
          x: 0.5,
          y: 0.56,
          title: '4月から11月の鯉',
          body: '4月から11月には色とりどりの鯉が優雅に泳ぐ姿を見ることができます。',
          source: 'https://www.hida-kankou.jp/spot/278',
          mapQuery: '瀬戸川 白壁土蔵街',
        },
        {
          id: 'setogawa-ettou',
          x: 0.24,
          y: 0.4,
          title: '冬は鯉がいない',
          body: '鯉は例年11月下旬から4月上旬まで、越冬のため増島城跡の池に移されます。春には再びこの川に戻ります。',
          source: 'https://www.hida-kankou.jp/spot/278',
          mapQuery: '飛騨古川',
        },
        {
          id: 'setogawa-shiki',
          x: 0.76,
          y: 0.34,
          title: '四季の趣',
          body: '春の新緑、夏の涼やかな水音、秋の紅葉、冬の雪景色と、四季折々に異なる趣を楽しめます。',
          source: 'https://www.hida-kankou.jp/spot/278',
          mapQuery: '飛騨市 古川町',
        },
      ],
    },
    {
      id: 'gero-onsen',
      name: '下呂温泉 飛騨川の噴泉池',
      area: '下呂市・夕',
      image: '/assets/scenery/gifu/scenes/09_gero_onsen.webp',
      hotspots: [
        {
          id: 'gero-sanmeisen',
          x: 0.5,
          y: 0.5,
          title: '天下の三名泉',
          body: '下呂温泉は有馬、草津とともに「天下の三名泉」と称されました。',
          source: 'https://www.city.gero.lg.jp/site/kanko/1258.html',
          mapQuery: '下呂温泉',
        },
        {
          id: 'gero-ph9',
          x: 0.24,
          y: 0.64,
          title: 'pH9以上のアルカリ性',
          body: '泉質はアルカリ性単純温泉。pH値9以上というアルカリ性特有の石鹸効果があり、無色透明でほんのりとした湯の香りがあります。「健康の湯」「美人の湯」と呼ばれ、現在は55度の温泉を各旅館に配湯しています。',
          source: 'https://www.city.gero.lg.jp/site/kanko/1258.html',
          mapQuery: '下呂温泉 泉質',
        },
        {
          id: 'gero-shirasagi',
          x: 0.76,
          y: 0.3,
          title: '白鷺の伝説',
          body: '薬師如来が傷ついた一羽の白鷺に姿を変え、飛騨川で傷を癒して源泉のありかを村人に知らせたと伝わります。温泉街の真ん中を流れる飛騨川の河川敷にある噴泉池が、下呂温泉のシンボルです。',
          source: 'https://www.city.gero.lg.jp/site/kanko/1258.html',
          mapQuery: '下呂温泉 噴泉池',
        },
      ],
    },
    {
      id: 'nagaragawa-ukai',
      name: 'ぎふ長良川鵜飼',
      area: '岐阜市・夏の夜',
      image: '/assets/scenery/gifu/scenes/10_nagaragawa_ukai.webp',
      hotspots: [
        {
          id: 'ukai-shitei',
          x: 0.5,
          y: 0.5,
          title: '国の重要無形民俗文化財',
          body: '「長良川の鵜飼漁の技術」は民俗技術（生産・生業）として、平成27年3月2日に指定されました。',
          source: 'https://www.city.gifu.lg.jp/kankoubunka/bunkazai/1013873/1013874.html',
          mapQuery: 'ぎふ長良川鵜飼',
        },
        {
          id: 'ukai-usho',
          x: 0.26,
          y: 0.38,
          title: '3人と10から12羽',
          body: '鵜匠、艫乗り、中乗りの3人が鵜舟という木造船に乗って川を下りながら漁を行い、10から12羽の鵜が用いられます。鵜匠は世襲での技術の継承を原則としています。',
          source: 'https://www.city.gifu.lg.jp/kankoubunka/bunkazai/1013873/1013874.html',
          mapQuery: '長良川 鵜匠',
        },
        {
          id: 'ukai-kagari',
          x: 0.74,
          y: 0.3,
          title: '両頭船と松割木',
          body: '鵜舟は水に強くて軽いコウヤマキを素材とし、川を下りながら漁をするのに合わせて舳と艫の形が似た両頭船の形を保っています。篝は鉄製の籠とヒノキの篝棒からなり、松割木を燃料とします。装束は麻の風折烏帽子、紺木綿の漁服と胸当、藁の腰蓑と足半です。',
          source: 'https://www.city.gifu.lg.jp/kankoubunka/bunkazai/1013873/1013874.html',
          mapQuery: '長良川 鵜舟',
        },
      ],
    },
  ],
};
